import type { InferResponseType } from "hono/client";
import type { api } from "@/client/lib/api";
import type { SessionSnapshot } from "@/features/identity/schemas";

type WorkingSetPage = InferResponseType<typeof api.tasks.$get, 200>;

/** What `public/early-reads.js` asked for before the app's code ran; null once handed over. */
interface EarlyReads {
  session: Promise<SessionSnapshot | null> | null;
  tasks: Promise<{ slug: string; page: WorkingSetPage } | null> | null;
}

declare global {
  interface Window {
    earlyReads?: EarlyReads;
  }
}

/**
 * Handed over once, to the first read of its kind on the page load's first screen. Null when the
 * page asked for nothing, the answer was a refusal, or the first screen has drawn.
 */
export function takeEarly<K extends keyof EarlyReads>(key: K): EarlyReads[K] {
  const reads = window.earlyReads;
  if (!reads) return null;
  const read = reads[key];
  reads[key] = null;
  return read;
}

/**
 * Called once the first screen has drawn. A read the load left unused (the working set restored from
 * this browser's copy) would otherwise answer a later refetch, or another login on this tab, with
 * what the server said at page load.
 */
export function forgetEarly(): void {
  window.earlyReads = undefined;
}
